/**
 * One row-click contract for every grid in LabBOXX.
 *
 * Clicking a row opens that record for view/edit. The Actions column stays
 * where it is — the row click is a second way to reach the same screen, not a
 * replacement for the buttons, so keyboard users keep the path they had.
 *
 * The whole difficulty is telling "open this record" apart from the clicks
 * that merely happen to land inside a row: the pencil, the delete button, a
 * checkbox, a link, the trigger of a confirm dialog. Those bubble up to the
 * <tr> like any other click, so a naive row handler fires the row action ON
 * TOP of the button's own — you press Delete and get navigated away mid
 * confirm. `shouldIgnoreRowClick` is the guard, and it lives here rather than
 * in each page so the answer is the same on all of them.
 */
import * as React from "react"

import { cn } from "./utils"

/** Elements inside a row that already own their click. `data-row-noopen` on
 *  any wrapper opts a cell out where its markup is none of these. */
const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="combobox"]',
  '[role="switch"]',
  '[role="tab"]',
  "[data-row-noopen]",
].join(",")

/**
 * True when this click must NOT open the row.
 *
 * Call it from the row's own onClick, where `currentTarget` is the row.
 */
export function shouldIgnoreRowClick(
  e: React.MouseEvent<HTMLElement>
): boolean {
  // Ctrl/Cmd/Shift-click and anything that is not the primary button belong to
  // the browser (new tab, extend selection). Swallowing them would make the
  // grid feel broken in exactly the ways power users notice.
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
    return true
  }

  const row = e.currentTarget
  const target = e.target as HTMLElement | null
  if (!target) return true

  // `closest` can walk out of the row (a table nested in a [role=tab] panel),
  // so only honour a hit the row actually contains.
  const interactive = target.closest(INTERACTIVE_SELECTOR)
  if (interactive && row.contains(interactive)) return true

  // A click that ends a drag-select inside this row is someone copying an
  // accession number, not someone opening the record.
  const selection = typeof window !== "undefined" ? window.getSelection() : null
  if (selection && !selection.isCollapsed && selection.toString().trim()) {
    const anchor = selection.anchorNode
    const anchorEl =
      anchor && anchor.nodeType === Node.ELEMENT_NODE
        ? (anchor as Element)
        : anchor?.parentElement ?? null
    if (anchorEl && row.contains(anchorEl)) return true
  }

  return false
}

/**
 * Spread onto a hand-rolled <TableRow>/<tr> to give it the same behaviour the
 * shared <DataTable> gets for free.
 *
 *   <TableRow key={t.id} {...rowOpenProps(() => handleEdit(t))}>
 *
 * Pass `undefined` for rows that must not open (a totals line, a record whose
 * detail screen does not exist yet) and the row is left untouched.
 */
export function rowOpenProps(
  open: (() => void) | undefined,
  className?: string
): {
  className?: string
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  title?: string
} {
  if (!open) return { className }
  return {
    className: cn("cursor-pointer", className),
    onClick: (e) => {
      if (shouldIgnoreRowClick(e)) return
      open()
    },
  }
}
